/**
 * CSRF Token Helper — In-memory CSRF token management for cookie sessions
 *
 * Fetches CSRF token from backend and attaches to state-changing requests.
 * Token stored in module-scoped variable (not localStorage) for security.
 *
 * Pattern: Double-submit — backend sets `11to1_csrf` cookie and returns token
 * in response body. Frontend attaches token via `X-CSRF-Token` header.
 *
 * APP-W01b: token/promise state is scoped BY identity generation. Identity A's
 * pending CSRF fetch and its finally-cleanup can never clear or overwrite
 * identity B's promise or token, because each generation owns its own slot
 * and a generation transition retires the old slot exactly once (no
 * cross-generation finally side effects). Token value itself never carries
 * identity provenance — public CSRF issuance is NOT identity (W01a) — only
 * the in-flight ownership is scoped. No persistence anywhere.
 */

import { isAuthenticating, getAuthenticatingApiOrigin } from './api-config';
import { boundedFetch, boundedAuthDeadline, API_TOTAL_DEADLINE_MS } from './transport';

/** Module-scoped CSRF token — never persisted to storage */
let csrfToken: string | null = null;

/** Per-generation in-flight CSRF fetch (dedup within one identity). */
interface CsrfGenerationSlot {
  generation: number;
  promise: Promise<string | null> | null;
  /** Invalidate listener remove fn — cuts the slot loose on transitions. */
  detach: () => void;
}

/** The current generation's slot, or null before the first scoped fetch. */
let currentSlot: CsrfGenerationSlot | null = null;

/** Generation bound for the NEXT fetch (provider records it after advance). */
let latestObservedGeneration = 0;

function retireCurrentSlot(): void {
  if (currentSlot) {
    currentSlot.detach();
    currentSlot = null;
  }
}

/**
 * Advance the observed identity generation. The provider calls this after
 * every confirmed-identity advance; it retires the previous generation's
 * slot (and with it any of that generation's finally-cleanup authority) so
 * a stale `clearCsrfToken` cannot touch the newer identity's state.
 */
export function notifyCsrfGeneration(generation: number): void {
  latestObservedGeneration = generation;
  if (currentSlot && currentSlot.generation !== generation) {
    retireCurrentSlot();
  }
}

/**
 * Fetch a CSRF token from the backend.
 * Sets the `11to1_csrf` cookie (via Set-Cookie) and returns token in body.
 * Deduplicates concurrent calls WITHIN one identity generation; a new
 * generation starts its own slot.
 *
 * Non-authenticating builds (preview / fail-closed config) never fetch: the
 * call returns null without touching the network.
 */
export async function fetchCsrfToken(): Promise<string | null> {
  if (!isAuthenticating()) return null;

  // Deduplicate concurrent calls within the SAME generation only. A pending
  // fetch captured by a superseded identity never serves the new one — but
  // the response body it may still complete with CANNOT be consumed into
  // the new generation's slot either: only the winning slot fulfills.
  if (currentSlot?.promise && currentSlot.generation === latestObservedGeneration) {
    return currentSlot.promise;
  }

  const generation = latestObservedGeneration;
  const ownDeferred = createCompletion();
  const slot: CsrfGenerationSlot = {
    generation,
    promise: ownDeferred.promise,
    detach: () => {
      /* slot detach is idempotent */
    },
  };
  currentSlot = slot;

  const run = (async () => {
    try {
      const outcome = await boundedFetch(`${getAuthenticatingApiOrigin()}/auth/csrf`, {
        method: 'GET',
        deadlineMs: boundedAuthDeadline(API_TOTAL_DEADLINE_MS),
      });

      // A superseded generation's result is invisible to the new identity.
      if (currentSlot !== slot) {
        return null;
      }

      if (outcome.kind !== 'response' || !outcome.ok) {
        csrfToken = null;
        return null;
      }

      let data: unknown;
      try {
        data = JSON.parse(outcome.bodyText);
      } catch {
        csrfToken = null;
        return null;
      }
      const token =
        typeof data === 'object' && data !== null && 'csrfToken' in data
          ? (data as { csrfToken?: unknown }).csrfToken
          : null;
      csrfToken = typeof token === 'string' && token.length > 0 ? token : null;
      return csrfToken;
    } catch {
      if (currentSlot === slot) {
        csrfToken = null;
      }
      return null;
    } finally {
      // Only THIS generation's finally may retire THIS generation's slot;
      // B's finally must never clear A's promise/token.
      if (currentSlot === slot) {
        currentSlot = null;
      }
    }
  })();

  // Complete the dedup promise from this run's OWN result. A caller that
  // captured this slot under a since-superseded identity is itself stale and
  // fenced by its own generation checks; the run's result never crosses
  // into a newer generation's state (its consumers were already replaced).
  void run.then((token) => ownDeferred.resolve(token));

  return ownDeferred.promise;
}

function createCompletion() {
  let resolvePromise!: (token: string | null) => void;
  const promise = new Promise<string | null>((res) => {
    resolvePromise = res;
  });
  return { promise, resolve: resolvePromise };
}

/** Get the current in-memory CSRF token (may be null if not yet fetched) */
export function getCsrfToken(): string | null {
  return csrfToken;
}

/** Clear the CSRF token (call on logout) */
export function clearCsrfToken(): void {
  csrfToken = null;
  retireCurrentSlot();
}

/** Check if an HTTP method requires CSRF protection */
export function isStateChangingMethod(method?: string): boolean {
  if (!method) return false;
  const upper = method.toUpperCase();
  return upper === 'POST' || upper === 'PUT' || upper === 'PATCH' || upper === 'DELETE';
}

/**
 * Check if a valid in-memory CSRF token is present.
 *
 * Session-proof semantics are REMOVED (W01b): token presence is no longer a
 * claim that the backend accepted any session. Callers needing liveness must
 * consult the identity provider, not CSRF issuance state.
 */
export function isCookieSessionActive(): boolean {
  return csrfToken !== null;
}

/**
 * Synchronous check for CSRF cookie in document.cookie.
 * Used on page reload to detect if a cookie session may still be valid
 * (the CSRF cookie is non-httpOnly, so JS can read it).
 */
export function hasCsrfCookie(): boolean {
  try {
    return document.cookie.includes('11to1_csrf=');
  } catch {
    return false;
  }
}

/**
 * Read the CSRF cookie value from document.cookie (non-httpOnly by design).
 * Fallback for when the in-memory token was never fetched this page load —
 * the double-submit check only requires header === cookie.
 */
export function readCsrfCookie(): string | null {
  try {
    const match = document.cookie.match(/(?:^|;\s*)11to1_csrf=([^;]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}
